
// =========================================================
// DEBUTS CROP
// PROFILE.jsx
//
// ROLE-BASED PROFILE CENTER
// PREMIUM NEON / RETRO TROPICAL
//
// ROLES:
//   - ADMIN
//   - TRABAJADOR
//   - CLIENTE
//   - OTROS ROLES
//
// FUNCIONES:
//   - Autenticación JWT
//   - Perfil dinámico
//   - Estadísticas
//   - Accesos por rol
//   - Panel de administración
//   - Administración central de tiendas
//   - Acceso para crear tiendas
//   - Gestión comercial de tiendas
//   - Accesos operativos
//   - Pedidos, favoritos y reseñas
//   - Seguridad y cierre de sesión
//   - Gráfica visual de estadísticas
// =========================================================

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Activity,
  ArrowLeft,
  BarChart3,
  Box,
  Camera,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  CreditCard,
  Heart,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MapPin,
  Package,
  PackageCheck,
  PackageSearch,
  RefreshCw,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Star,
  Store,
  User,
  UserCog,
  UserRound,
  Users,
  Wallet,
  Plus,
  Building2,
  ChartNoAxesCombined,
} from "lucide-react";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import logo from "../../assets/logo.png";

import "../../styles/profile.css";

// =========================================================
// API
// =========================================================

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? "http://localhost:8000/api"
      : "https://api.debutscrop.com/api"
  );

// =========================================================
// UTILIDADES
// =========================================================

const normalizeRole = (role) => {
  const value = String(role || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s-]+/g, "_");

  const aliases = {
    admin: "admin",
    administrador: "admin",
    superadmin: "admin",
    super_admin: "admin",

    trabajador: "trabajador",
    empleado: "trabajador",
    worker: "trabajador",
    staff: "trabajador",

    cliente: "cliente",
    customer: "cliente",
    user: "cliente",
    usuario: "cliente",
  };

  return aliases[value] || value || "cliente";
};

// =========================================================
// EXTRAER LISTAS DE RESPUESTAS API
// =========================================================

const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

// =========================================================
// OBTENER USUARIO ALMACENADO
// =========================================================

const getStoredUser = (auth) => {
  if (auth?.user && typeof auth.user === "object") {
    return auth.user;
  }

  if (auth?.profile && typeof auth.profile === "object") {
    return auth.profile;
  }

  if (auth?.data?.user && typeof auth.data.user === "object") {
    return auth.data.user;
  }

  try {
    const rawUser = localStorage.getItem("user");

    if (!rawUser) return null;

    return JSON.parse(rawUser);
  } catch {
    return null;
  }
};

// =========================================================
// FORMATEAR FECHAS
// =========================================================

const formatMemberDate = (rawDate) => {
  if (!rawDate) {
    return "MIEMBRO DE DEBUTS CROP";
  }

  const date = new Date(rawDate);

  if (Number.isNaN(date.getTime())) {
    return "MIEMBRO DE DEBUTS CROP";
  }

  const formatted = new Intl.DateTimeFormat("es-CO", {
    month: "long",
    year: "numeric",
  }).format(date);

  return `Desde ${
    formatted.charAt(0).toUpperCase() + formatted.slice(1)
  }`;
};

// =========================================================
// PROFILE
// =========================================================

export default function Profile() {
  const navigate = useNavigate();

  // =======================================================
  // USER
  // =======================================================

  const [user, setUser] = useState(null);

  // =======================================================
  // PROFILE STATS
  // =======================================================

  const [profileStats, setProfileStats] = useState({
    orders: 0,
    favorites: 0,
    reviews: 0,
  });

  // =======================================================
  // LOADING
  // =======================================================

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  // =======================================================
  // AUTHENTICATION
  // =======================================================

  useEffect(() => {
    try {
      const authRaw = localStorage.getItem("auth");

      if (!authRaw) {
        navigate("/login", { replace: true });
        return;
      }

      const auth = JSON.parse(authRaw);

      if (!auth?.access) {
        navigate("/login", { replace: true });
        return;
      }

      const storedUser = getStoredUser(auth);

      if (!storedUser) {
        navigate("/login", { replace: true });
        return;
      }

      setUser(storedUser);
    } catch (error) {
      console.error("Error cargando el perfil:", error);

      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      localStorage.removeItem("redirectAfterLogin");

      window.dispatchEvent(new Event("authUpdated"));

      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // =======================================================
  // CARGAR ESTADÍSTICAS
  // =======================================================

  useEffect(() => {
    let cancelled = false;

    const loadProfileStats = async () => {
      setStatsLoading(true);
      setStatsError(false);

      try {
        const authRaw = localStorage.getItem("auth");

        if (!authRaw) {
          if (!cancelled) setStatsLoading(false);
          return;
        }

        const auth = JSON.parse(authRaw);
        const accessToken = auth?.access;

        if (!accessToken) {
          if (!cancelled) setStatsLoading(false);
          return;
        }

        const headers = {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        };

        const endpoints = [
          {
            key: "orders",
            url: `${API_BASE_URL}/checkout/`,
          },
          {
            key: "favorites",
            url: `${API_BASE_URL}/reviews/favorites/`,
          },
          {
            key: "reviews",
            url: `${API_BASE_URL}/reviews/reviews/`,
          },
        ];

        const results = await Promise.allSettled(
          endpoints.map(async (endpoint) => {
            const response = await fetch(endpoint.url, {
              method: "GET",
              headers,
            });

            if (!response.ok) {
              throw new Error(
                `${endpoint.key}: HTTP ${response.status}`
              );
            }

            return {
              key: endpoint.key,
              data: await response.json(),
            };
          })
        );

        const nextStats = {
          orders: 0,
          favorites: 0,
          reviews: 0,
        };

        let failedRequests = 0;

        results.forEach((result, index) => {
          if (result.status === "rejected") {
            failedRequests += 1;

            console.error(
              `Error cargando ${endpoints[index].key}:`,
              result.reason
            );

            return;
          }

          const { key, data } = result.value;
          nextStats[key] = extractList(data).length;
        });

        if (!cancelled) {
          setProfileStats(nextStats);
          setStatsError(failedRequests === endpoints.length);
        }
      } catch (error) {
        console.error("Error cargando estadísticas:", error);

        if (!cancelled) {
          setStatsError(true);

          setProfileStats({
            orders: 0,
            favorites: 0,
            reviews: 0,
          });
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };

    loadProfileStats();

    return () => {
      cancelled = true;
    };
  }, []);

  // =======================================================
  // USER NAME
  // =======================================================

  const userName = useMemo(() => {
    if (!user) return "USUARIO DEBUTS";

    return (
      user.full_name ||
      user.name ||
      user.username ||
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      "USUARIO DEBUTS"
    );
  }, [user]);

  // =======================================================
  // USER EMAIL
  // =======================================================

  const userEmail = useMemo(() => {
    return user?.email || "Correo no registrado";
  }, [user]);

  // =======================================================
  // USER ROLE
  // =======================================================

  const userRole = useMemo(() => {
    return normalizeRole(
      user?.role ||
      user?.user_type ||
      user?.tipo_usuario ||
      user?.account_type ||
      (user?.is_superuser ? "admin" : "") ||
      (user?.is_staff ? "trabajador" : "cliente")
    );
  }, [user]);

  // =======================================================
  // ROLE LABEL
  // =======================================================

  const roleLabel = useMemo(() => {
    const labels = {
      admin: "ADMINISTRADOR",
      trabajador: "TRABAJADOR",
      cliente: "CLIENTE",
    };

    return labels[userRole] || "MIEMBRO";
  }, [userRole]);

  // =======================================================
  // ROLE DESCRIPTION
  // =======================================================

  const roleDescription = useMemo(() => {
    const descriptions = {
      admin: "Centro de control y administración",
      trabajador: "Centro de operaciones",
      cliente: "Tu espacio personal",
    };

    return (
      descriptions[userRole] ||
      "Tu espacio personal en Debuts Crop"
    );
  }, [userRole]);

  // =======================================================
  // ROLE PERMISSIONS FOR THE UI
  // =======================================================

  const isAdmin = userRole === "admin";
  const isWorker = userRole === "trabajador";
  const isCustomer = userRole === "cliente";

  // =======================================================
  // AVATAR INITIAL
  // =======================================================

  const firstLetter = useMemo(() => {
    return userName.charAt(0).toUpperCase();
  }, [userName]);

  // =======================================================
  // MEMBER SINCE
  // =======================================================

  const memberSince = useMemo(() => {
    const rawDate =
      user?.date_joined ||
      user?.created_at ||
      user?.created ||
      user?.registered_at;

    return formatMemberDate(rawDate);
  }, [user]);

  // =======================================================
  // FORMAT STATS
  // =======================================================

  const formatStat = (value) => {
    return statsLoading ? "—" : value;
  };

  // =======================================================
  // LOGOUT
  // =======================================================

  const handleLogout = () => {
    try {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      localStorage.removeItem("redirectAfterLogin");

      window.dispatchEvent(new Event("authUpdated"));
    } catch (error) {
      console.error("Error cerrando sesión:", error);
    }

    navigate("/login", { replace: true });
  };

  // =======================================================
  // NAVIGATION
  // =======================================================

  const goBack = () => {
    navigate(-1);
  };

  const goToDashboard = () => {
    navigate("/dashboard");
  };

  const goToStores = () => {
    navigate("/stores-admin");
  };

  const goToCreateStore = () => {
    navigate("/stores-admin/new");
  };

  const goToStoreReports = () => {
    navigate("/dashboard/reports");
  };

  // =======================================================
  // ADMIN MENU
  //
  // Verifica que estas rutas estén declaradas en App.jsx.
  // =======================================================

  const adminTools = [
    {
      title: "Panel de administración",
      description: "Resumen general del negocio",
      icon: LayoutDashboard,
      path: "/dashboard",
    },
    {
      title: "Tiendas",
      description: "Administrar tiendas, propietarios, ubicación y estado",
      icon: Store,
      path: "/stores-admin",
    },
    {
      title: "Crear tienda",
      description: "Registrar una nueva tienda en Debuts Crop",
      icon: Plus,
      path: "/stores-admin/new",
    },
    {
      title: "Gestión comercial",
      description: "Acceso a indicadores y reportes del negocio",
      icon: ChartNoAxesCombined,
      path: "/dashboard/reports",
    },
 
    {
      title: "Gestor de trabajadores",
      description: "Registrar, consultar y administrar el personal",
      icon: Users,
      path: "/workers-admin",
    },

    
    {
      title: "Inventario",
      description: "Control de existencias y disponibilidad",
      icon: Box,
      path: "/inventory",
    },
    {
      title: "Productos",
      description: "Administrar catálogo, precios, imágenes y categorías",
      icon: ShoppingBag,
      path: "/products-admin",
    },
    {
      title: "Pedidos",
      description: "Pedidos y estados de compra",
      icon: ClipboardList,
      path: "/orders",
    },
    {
      title: "Ventas",
      description: "Gestión de ventas y transacciones",
      icon: Wallet,
      path: "/sales",
    },
    {
      title: "Clientes",
      description: "Usuarios y relaciones con clientes",
      icon: Users,
      path: "/customers",
    },
    {
      title: "Reportes y estadísticas",
      description: "Indicadores y análisis del negocio",
      icon: BarChart3,
      path: "/dashboard/reports",
    },
    {
      title: "Configuración",
      description: "Ajustes del sistema",
      icon: Settings,
      path: "/settings",
    },
  ];

  // =======================================================
  // WORKER MENU
  // =======================================================

  const workerTools = [
    {
      title: "Pedidos por gestionar",
      description: "Consulta y seguimiento de pedidos",
      icon: ClipboardList,
      path: "/orders",
    },
    {
      title: "Inventario",
      description: "Consulta de productos y existencias",
      icon: PackageSearch,
      path: "/inventory",
    },
    {
      title: "Preparación de pedidos",
      description: "Acceso al flujo operativo",
      icon: PackageCheck,
      path: "/orders",
    },
    {
      title: "Entregas",
      description: "Seguimiento de pedidos para entrega",
      icon: MapPin,
      path: "/orders",
    },
    {
      title: "Ventas",
      description: "Consulta de operaciones comerciales",
      icon: CreditCard,
      path: "/sales",
    },
  ];

  // =======================================================
  // CUSTOMER MENU
  // =======================================================

  const customerTools = [
    {
      title: "Mis pedidos",
      description: "Historial y seguimiento",
      icon: Package,
      path: "/orders",
    },
    {
      title: "Mis favoritos",
      description: "Productos que guardaste",
      icon: Heart,
      path: "/favorites",
    },
    {
      title: "Mis reseñas",
      description: "Opiniones y valoraciones",
      icon: Star,
      path: "/reviews",
    },
    {
      title: "Mis direcciones",
      description: "Direcciones de envío",
      icon: MapPin,
      path: "/addresses",
    },
  ];

  // =======================================================
  // TOOLS POR ROL
  // =======================================================

  const roleTools = isAdmin
    ? adminTools
    : isWorker
      ? workerTools
      : customerTools;

  // =======================================================
  // GRAPH DATA
  // =======================================================

  const chartData = useMemo(() => {
    const values = [
      {
        label: "Pedidos",
        value: profileStats.orders,
        color: "orders",
      },
      {
        label: "Favoritos",
        value: profileStats.favorites,
        color: "favorites",
      },
      {
        label: "Reseñas",
        value: profileStats.reviews,
        color: "reviews",
      },
    ];

    const maximum = Math.max(
      1,
      ...values.map((item) => item.value)
    );

    return values.map((item) => ({
      ...item,
      width: Math.max(
        item.value > 0 ? 4 : 0,
        (item.value / maximum) * 100
      ),
    }));
  }, [profileStats]);

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <>
      <Navbar />

      <main className="profile-page">
        <div className="profile-background-overlay" />

        <div className="profile-bg-glow profile-bg-glow-one" />
        <div className="profile-bg-glow profile-bg-glow-two" />
        <div className="profile-bg-glow profile-bg-glow-three" />

        <div className="profile-page-container">

          {/* TITLE */}

          <section className="profile-title-bar">
            <button
              type="button"
              className="profile-back-button"
              onClick={goBack}
              aria-label="Volver"
            >
              <ArrowLeft size={22} />
            </button>

            <div className="profile-title-content">
              <span className="profile-title-small">
                DEBUTS CROP · {roleLabel}
              </span>

              <h1>MI PERFIL</h1>

              <div className="profile-title-line" />
            </div>

            <button
              type="button"
              className="profile-settings-button"
              onClick={() => navigate("/settings")}
            >
              <Settings size={19} />
              <span>Configuración</span>
            </button>
          </section>

          {/* MAIN LAYOUT */}

          <section className="profile-layout">

            {/* SIDEBAR */}

            <aside className="profile-sidebar">
              <nav className="profile-sidebar-menu">

                <button
                  type="button"
                  className="profile-sidebar-item active"
                  onClick={() => navigate("/profile")}
                >
                  <span className="profile-sidebar-icon">
                    <User size={20} />
                  </span>
                  <span>Mi perfil</span>
                </button>

                {/* ADMINISTRATION */}

                {isAdmin && (
                  <>
                    <button
                      type="button"
                      className="profile-sidebar-item profile-admin-item"
                      onClick={goToDashboard}
                    >
                      <span className="profile-sidebar-icon">
                        <LayoutDashboard size={20} />
                      </span>
                      <span>Administración</span>
                      <ChevronRight size={17} />
                    </button>

                    {/* NUEVO: TIENDAS */}

                    <button
                      type="button"
                      className="profile-sidebar-item profile-admin-item"
                      onClick={goToStores}
                    >
                      <span className="profile-sidebar-icon">
                        <Store size={20} />
                      </span>
                      <span>Tiendas</span>
                      <ChevronRight size={17} />
                    </button>
                    

                    {/* NUEVO: CREAR TIENDA */}

                    <button
                      type="button"
                      className="profile-sidebar-item"
                      onClick={goToCreateStore}
                    >
                      <span className="profile-sidebar-icon">
                        <Plus size={20} />
                      </span>
                      <span>Crear tienda</span>
                      <ChevronRight size={17} />
                    </button>
                  </>
                )}

                {/* WORKER CENTER */}

                {isWorker && (
                  <button
                    type="button"
                    className="profile-sidebar-item"
                    onClick={() => navigate("/orders")}
                  >
                    <span className="profile-sidebar-icon">
                      <Activity size={20} />
                    </span>
                    <span>Operaciones</span>
                    <ChevronRight size={17} />
                  </button>
                )}

                {/* ORDERS */}

                <button
                  type="button"
                  className="profile-sidebar-item"
                  onClick={() => navigate("/orders")}
                >
                  <span className="profile-sidebar-icon">
                    <Package size={20} />
                  </span>
                  <span>
                    {isCustomer ? "Mis pedidos" : "Pedidos"}
                  </span>
                  <ChevronRight size={17} />
                </button>

                {/* CUSTOMER FAVORITES */}

                {isCustomer && (
                  <button
                    type="button"
                    className="profile-sidebar-item"
                    onClick={() => navigate("/favorites")}
                  >
                    <span className="profile-sidebar-icon">
                      <Heart size={20} />
                    </span>
                    <span>Favoritos</span>
                    <ChevronRight size={17} />
                  </button>
                )}

                {/* INVENTORY */}

                {(isAdmin || isWorker) && (
                  <button
                    type="button"
                    className="profile-sidebar-item"
                    onClick={() => navigate("/inventory")}
                  >
                    <span className="profile-sidebar-icon">
                      <Box size={20} />
                    </span>
                    <span>Inventario</span>
                    <ChevronRight size={17} />
                  </button>
                )}

                {/* ADMIN REPORTS */}

                {isAdmin && (
                  <button
                    type="button"
                    className="profile-sidebar-item"
                    onClick={goToStoreReports}
                  >
                    <span className="profile-sidebar-icon">
                      <BarChart3 size={20} />
                    </span>
                    <span>Reportes</span>
                    <ChevronRight size={17} />
                  </button>
                )}

                {/* CUSTOMER ADDRESSES */}

                {isCustomer && (
                  <button
                    type="button"
                    className="profile-sidebar-item"
                    onClick={() => navigate("/addresses")}
                  >
                    <span className="profile-sidebar-icon">
                      <MapPin size={20} />
                    </span>
                    <span>Direcciones</span>
                    <ChevronRight size={17} />
                  </button>
                )}
              </nav>

              <div className="profile-sidebar-divider" />

              <div className="profile-sidebar-section-title">
                CUENTA Y SEGURIDAD
              </div>

              <button
                type="button"
                className="profile-sidebar-item"
                onClick={() => navigate("/profile/edit")}
              >
                <span className="profile-sidebar-icon">
                  <UserCog size={20} />
                </span>
                <span>Información personal</span>
                <ChevronRight size={17} />
              </button>

              <button
                type="button"
                className="profile-sidebar-item"
                onClick={() => navigate("/change-password")}
              >
                <span className="profile-sidebar-icon">
                  <KeyRound size={20} />
                </span>
                <span>Contraseña</span>
                <ChevronRight size={17} />
              </button>

              <button
                type="button"
                className="profile-sidebar-item profile-logout"
                onClick={handleLogout}
              >
                <span className="profile-sidebar-icon">
                  <LogOut size={20} />
                </span>
                <span>Cerrar sesión</span>
                <ChevronRight size={17} />
              </button>

              {/* BRAND */}

              <div className="profile-sidebar-brand">
                <div className="profile-sidebar-brand-image">
                  <img src={logo} alt="Debuts Crop" />
                </div>

                <span>TU CULTIVO · TU ESTILO</span>
              </div>
            </aside>

            {/* MAIN CONTENT */}

            <div className="profile-content">

              {/* USER CARD */}

              <section className="profile-user-card">
                <div className="profile-user-background" />

                <div className="profile-user-inner">
                  <div className="profile-avatar-wrapper">
                    <div className="profile-avatar">
                      <span>{firstLetter}</span>
                    </div>

                    <button
                      type="button"
                      className="profile-avatar-camera"
                      aria-label="Cambiar foto de perfil"
                      onClick={() => navigate("/profile/edit")}
                    >
                      <Camera size={14} />
                    </button>
                  </div>

                  <div className="profile-user-info">
                    <div className="profile-user-name-row">
                      <h2>{userName}</h2>

                      {isAdmin ? (
                        <ShieldCheck
                          size={22}
                          className="profile-role-icon"
                        />
                      ) : isWorker ? (
                        <CheckCircle2
                          size={22}
                          className="profile-role-icon"
                        />
                      ) : null}
                    </div>

                    <p className="profile-user-email">
                      {userEmail}
                    </p>

                    <div className="profile-member-badge">
                      {roleLabel}
                    </div>

                    <span className="profile-member-date">
                      {memberSince}
                    </span>
                  </div>

                  <div className="profile-user-slogan">
                    <strong>TU CULTIVO</strong>
                    <strong>TU ESTILO</strong>
                  </div>
                </div>
              </section>

              {/* ROLE WELCOME */}

              <section className="profile-panel profile-role-panel">
                <div className="profile-panel-header">
                  <span className="profile-panel-header-icon">
                    {isAdmin ? (
                      <LayoutDashboard size={20} />
                    ) : isWorker ? (
                      <Store size={20} />
                    ) : (
                      <UserRound size={20} />
                    )}
                  </span>

                  <h3>{roleDescription}</h3>
                </div>

                <p className="profile-role-description">
                  {isAdmin
                    ? "Accede a las herramientas de gestión del negocio, administra tiendas, productos, inventario, pedidos y operaciones comerciales."
                    : isWorker
                      ? "Consulta tus herramientas operativas para apoyar la gestión de pedidos y productos según los permisos asignados a tu cuenta."
                      : "Administra tu información personal, consulta tus compras y accede a tus productos favoritos."}
                </p>
              </section>

              {/* PROFILE STATISTICS */}

              <section className="profile-stats">
                <button
                  type="button"
                  className="profile-stat-card"
                  onClick={() => navigate("/orders")}
                >
                  <span className="profile-stat-icon">
                    <Package size={24} />
                  </span>

                  <span className="profile-stat-number">
                    {formatStat(profileStats.orders)}
                  </span>

                  <span className="profile-stat-label">PEDIDOS</span>

                  <ChevronRight
                    size={19}
                    className="profile-stat-arrow"
                  />
                </button>

                <button
                  type="button"
                  className="profile-stat-card"
                  onClick={() => navigate("/favorites")}
                >
                  <span className="profile-stat-icon">
                    <Heart size={24} />
                  </span>

                  <span className="profile-stat-number">
                    {formatStat(profileStats.favorites)}
                  </span>

                  <span className="profile-stat-label">FAVORITOS</span>

                  <ChevronRight
                    size={19}
                    className="profile-stat-arrow"
                  />
                </button>

                <button
                  type="button"
                  className="profile-stat-card"
                  onClick={() => navigate("/reviews")}
                >
                  <span className="profile-stat-icon">
                    <Star size={24} />
                  </span>

                  <span className="profile-stat-number">
                    {formatStat(profileStats.reviews)}
                  </span>

                  <span className="profile-stat-label">RESEÑAS</span>

                  <ChevronRight
                    size={19}
                    className="profile-stat-arrow"
                  />
                </button>
              </section>

              {/* STATISTICS GRAPH */}

              <section className="profile-panel profile-analytics-panel">
                <div className="profile-panel-header">
                  <span className="profile-panel-header-icon">
                    <BarChart3 size={20} />
                  </span>

                  <h3>Resumen de actividad</h3>

                  <button
                    type="button"
                    className="profile-refresh-button"
                    onClick={() => window.location.reload()}
                    aria-label="Actualizar estadísticas"
                    title="Actualizar estadísticas"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>

                {statsError && (
                  <p className="profile-role-description">
                    Algunas estadísticas no pudieron cargarse.
                    Comprueba los endpoints y los permisos de la API.
                  </p>
                )}

                <div className="profile-chart">
                  {chartData.map((item) => (
                    <div
                      className="profile-chart-row"
                      key={item.label}
                    >
                      <div className="profile-chart-label">
                        <span>{item.label}</span>
                        <strong>{formatStat(item.value)}</strong>
                      </div>

                      <div
                        className="profile-chart-track"
                        role="progressbar"
                        aria-label={item.label}
                        aria-valuemin={0}
                        aria-valuemax={Math.max(
                          1,
                          ...chartData.map((entry) => entry.value)
                        )}
                        aria-valuenow={item.value}
                      >
                        <div
                          className={`profile-chart-bar profile-chart-bar-${item.color}`}
                          style={{
                            width: statsLoading
                              ? "0%"
                              : `${item.width}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <p className="profile-chart-note">
                  Indicadores de actividad de la cuenta.
                  No representan ingresos ni ventas monetarias.
                </p>
              </section>

              {/* ROLE TOOLS */}

              <section className="profile-panel profile-tools-panel">
                <div className="profile-panel-header">
                  <span className="profile-panel-header-icon">
                    {isAdmin ? (
                      <LayoutDashboard size={20} />
                    ) : isWorker ? (
                      <Activity size={20} />
                    ) : (
                      <ShoppingCart size={20} />
                    )}
                  </span>

                  <h3>
                    {isAdmin
                      ? "Centro de administración"
                      : isWorker
                        ? "Centro de operaciones"
                        : "Mi espacio personal"}
                  </h3>
                </div>

                <div className="profile-tools-grid">
                  {roleTools.map((tool) => {
                    const Icon = tool.icon;

                    return (
                      <button
                        type="button"
                        className="profile-tool-card"
                        key={`${userRole}-${tool.title}`}
                        onClick={() => navigate(tool.path)}
                      >
                        <span className="profile-tool-icon">
                          <Icon size={23} />
                        </span>

                        <span className="profile-tool-content">
                          <strong>{tool.title}</strong>
                          <small>{tool.description}</small>
                        </span>

                        <ChevronRight
                          size={18}
                          className="profile-tool-arrow"
                        />
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* =============================================
                  NUEVO: ADMINISTRACIÓN CENTRAL DE TIENDAS
              ============================================= */}

              {isAdmin && (
                <section className="profile-panel profile-admin-panel">
                  <div className="profile-panel-header">
                    <span className="profile-panel-header-icon">
                      <Building2 size={20} />
                    </span>

                    <h3>Administración de tiendas</h3>
                  </div>

                  <p className="profile-role-description">
                    Administra los establecimientos de Debuts Crop,
                    sus propietarios, ubicación, identidad visual,
                    estado comercial y configuración.
                  </p>

                  <div className="profile-panel-list">

                    {/* LISTAR TIENDAS */}

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={goToStores}
                    >
                      <span className="profile-panel-item-icon">
                        <Store size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Gestionar tiendas</strong>

                        <small>
                          Consultar, buscar, editar y administrar
                          las tiendas registradas
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    {/* GESTIÓN DE TRABAJADORES */}

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/workers-admin")}
                    >
                      <span className="profile-panel-item-icon">
                        <Users size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Gestión de trabajadores</strong>

                        <small>
                          Registrar, consultar y administrar el personal de Debuts Crop
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    {/* CREAR TIENDA */}

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={goToCreateStore}
                    >
                      <span className="profile-panel-item-icon">
                        <Plus size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Registrar nueva tienda</strong>

                        <small>
                          Crear una tienda con propietario,
                          contacto, dirección y datos comerciales
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    {/* PRODUCTOS */}

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/products-admin")}
                    >
                      <span className="profile-panel-item-icon">
                        <ShoppingBag size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Productos de las tiendas</strong>

                        <small>
                          Administrar catálogo, precios,
                          imágenes y disponibilidad
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    {/* INVENTARIO */}

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/inventory")}
                    >
                      <span className="profile-panel-item-icon">
                        <PackageSearch size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Inventario y existencias</strong>

                        <small>
                          Consultar las existencias administradas
                          por el sistema de inventario
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    {/* REPORTES */}

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={goToStoreReports}
                    >
                      <span className="profile-panel-item-icon">
                        <ChartNoAxesCombined size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Reportes comerciales</strong>

                        <small>
                          Acceder a los indicadores y reportes
                          habilitados en el sistema
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>
                  </div>
                </section>
              )}

              {/* ADMIN QUICK ACCESS */}

              {isAdmin && (
                <section className="profile-panel profile-admin-panel">
                  <div className="profile-panel-header">
                    <span className="profile-panel-header-icon">
                      <ShieldCheck size={20} />
                    </span>

                    <h3>Control del negocio</h3>
                  </div>

                  <div className="profile-panel-list">
                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/inventory")}
                    >
                      <span className="profile-panel-item-icon">
                        <Box size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Inventario y existencias</strong>

                        <small>
                          Acceso a la gestión de productos y stock
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/sales")}
                    >
                      <span className="profile-panel-item-icon">
                        <Wallet size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Ventas y transacciones</strong>

                        <small>
                          Consulta y gestión de operaciones
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/customers")}
                    >
                      <span className="profile-panel-item-icon">
                        <Users size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Gestión de clientes</strong>

                        <small>
                          Acceso a las herramientas de clientes
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/dashboard/reports")}
                    >
                      <span className="profile-panel-item-icon">
                        <BarChart3 size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Reportes del negocio</strong>

                        <small>
                          Acceso al módulo de análisis y estadísticas
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>
                  </div>
                </section>
              )}

              {/* WORKER QUICK ACCESS */}

              {isWorker && (
                <section className="profile-panel">
                  <div className="profile-panel-header">
                    <span className="profile-panel-header-icon">
                      <ClipboardList size={20} />
                    </span>

                    <h3>Herramientas de trabajo</h3>
                  </div>

                  <div className="profile-panel-list">
                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/orders")}
                    >
                      <span className="profile-panel-item-icon">
                        <PackageCheck size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Gestión de pedidos</strong>

                        <small>
                          Consulta el estado de las órdenes
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/inventory")}
                    >
                      <span className="profile-panel-item-icon">
                        <PackageSearch size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Consulta de inventario</strong>

                        <small>
                          Revisa los productos disponibles
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/sales")}
                    >
                      <span className="profile-panel-item-icon">
                        <CreditCard size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Operaciones de venta</strong>

                        <small>
                          Accede a las funciones comerciales permitidas
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>
                  </div>
                </section>
              )}

              {/* CUSTOMER ACCOUNT INFORMATION */}

              {isCustomer && (
                <section className="profile-panel">
                  <div className="profile-panel-header">
                    <span className="profile-panel-header-icon">
                      <UserRound size={20} />
                    </span>

                    <h3>Información de la cuenta</h3>
                  </div>

                  <div className="profile-panel-list">
                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/profile/edit")}
                    >
                      <span className="profile-panel-item-icon">
                        <User size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Información personal</strong>

                        <small>
                          Nombre, correo y datos personales
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/orders")}
                    >
                      <span className="profile-panel-item-icon">
                        <Package size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Mis pedidos</strong>

                        <small>
                          Historial y seguimiento de compras
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/favorites")}
                    >
                      <span className="profile-panel-item-icon">
                        <Heart size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Favoritos</strong>

                        <small>
                          Tus productos guardados
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>

                    <button
                      type="button"
                      className="profile-panel-item"
                      onClick={() => navigate("/addresses")}
                    >
                      <span className="profile-panel-item-icon">
                        <MapPin size={21} />
                      </span>

                      <span className="profile-panel-item-content">
                        <strong>Direcciones</strong>

                        <small>
                          Gestiona tus direcciones de envío
                        </small>
                      </span>

                      <ChevronRight size={19} />
                    </button>
                  </div>
                </section>
              )}

              {/* SECURITY */}

              <section className="profile-panel profile-security-panel">
                <div className="profile-panel-header">
                  <span className="profile-panel-header-icon">
                    <ShieldCheck size={20} />
                  </span>

                  <h3>Seguridad de la cuenta</h3>
                </div>

                <div className="profile-panel-list">
                  <button
                    type="button"
                    className="profile-panel-item"
                    onClick={() => navigate("/change-password")}
                  >
                    <span className="profile-panel-item-icon">
                      <KeyRound size={21} />
                    </span>

                    <span className="profile-panel-item-content">
                      <strong>Cambiar contraseña</strong>

                      <small>
                        Protege el acceso a tu cuenta
                      </small>
                    </span>

                    <ChevronRight size={19} />
                  </button>

                  <button
                    type="button"
                    className="profile-panel-item profile-panel-logout"
                    onClick={handleLogout}
                  >
                    <span className="profile-panel-item-icon">
                      <LogOut size={21} />
                    </span>

                    <span className="profile-panel-item-content">
                      <strong>Cerrar sesión</strong>

                      <small>
                        Salir de tu cuenta de forma segura
                      </small>
                    </span>

                    <ChevronRight size={19} />
                  </button>
                </div>
              </section>

            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
